import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import ClipDetailsModal from "./ClipDetailsModal";
import EditableLabel from "./EditableLabel";

const StatusPill = ({ status }) => {
  const map = {
    creating: { label: "Queuing", color: "text-zinc-400 bg-white/5" },
    in_progress: {
      label: "Generating",
      color: "text-yellow-400 bg-yellow-500/10",
    },
    stitching: {
      label: "Stitching",
      color: "text-yellow-400 bg-yellow-500/10",
    },
    done: { label: "Ready", color: "text-emerald-400 bg-emerald-500/10" },
    failed: { label: "Failed", color: "text-red-400 bg-red-500/10" },
  };
  const m = map[status] || map.creating;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono ${m.color}`}
    >
      {(status === "in_progress" || status === "stitching" || status === "creating") && (
        <span className="w-1 h-1 rounded-full bg-current animate-pulse" />
      )}
      {m.label}
    </span>
  );
};

export default function Library({
  jobs,
  reels,
  refreshJobs,
  refreshReels,
  onPlay,
  initialTab = "clips",
  listingId,
  onCloneReel,
}) {
  const [tab, setTab] = useState(initialTab);
  const [details, setDetails] = useState(null); // {item, kind}
  const [diagnose, setDiagnose] = useState(null); // null | {loading} | {data} | {error}
  const [importDialog, setImportDialog] = useState(false);
  const [importMode, setImportMode] = useState("clip"); // "clip" | "reel"
  const [importUrl, setImportUrl] = useState("");
  const [importLabel, setImportLabel] = useState("");
  const [importBusy, setImportBusy] = useState(false);

  const openImportDialog = (mode) => {
    setImportMode(mode || "clip");
    setImportUrl("");
    setImportLabel("");
    setImportDialog(true);
  };

  const submitImport = async () => {
    const url = importUrl.trim();
    if (!url) return;
    setImportBusy(true);
    try {
      if (importMode === "reel") {
        await api.post(`/listings/${listingId}/import-reel`, {
          source_url: url,
          title: importLabel.trim() || "Imported reel",
        });
        toast.success("Reel imported with fresh share token");
        refreshReels?.();
      } else {
        await api.post(`/listings/${listingId}/import-clip`, {
          source_url: url,
          label: importLabel.trim() || "Imported clip",
        });
        toast.success("Clip imported");
        refreshJobs?.();
      }
      setImportDialog(false);
      setImportUrl("");
      setImportLabel("");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || "Import failed"
      );
    } finally {
      setImportBusy(false);
    }
  };

  const runDiagnose = async () => {
    if (!listingId) return;
    setDiagnose({ loading: true });
    try {
      const { data } = await api.get(
        `/_diagnose/reveals?listing_id=${listingId}`
      );
      setDiagnose({ data });
    } catch (e) {
      setDiagnose({
        error:
          e?.response?.data?.detail ||
          e?.message ||
          "Diagnose endpoint unreachable (is production on the latest build?)",
      });
    }
  };

  const [cleanupBusy, setCleanupBusy] = useState(false);
  const runCleanupZombies = async () => {
    if (cleanupBusy) return;
    if (
      !window.confirm(
        "Mark every job/reel that's been stuck in_progress for >30 min as failed? They'll show a Retry button.",
      )
    ) {
      return;
    }
    setCleanupBusy(true);
    try {
      const { data } = await api.post(
        "/admin/cleanup-zombies?minutes=30",
      );
      const j = data?.jobs_cleaned || 0;
      const r = data?.reels_cleaned || 0;
      if (j === 0 && r === 0) {
        toast.success("No stuck jobs — all clean");
      } else {
        toast.success(`Cleaned ${j} job(s) and ${r} reel(s) — refresh to see Retry buttons`);
        refreshJobs?.();
        refreshReels?.();
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          "Cleanup failed (is the latest build deployed?)",
      );
    } finally {
      setCleanupBusy(false);
    }
  };

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    const id = setInterval(() => {
      refreshJobs();
      refreshReels();
    }, 6000);
    return () => clearInterval(id);
  }, [refreshJobs, refreshReels]);

  const tabs = [
    { id: "clips", label: "Single Clips", count: jobs.length },
    { id: "reels", label: "Listing Reels", count: reels.length },
  ];

  const onDeleteJob = async (id) => {
    await api.delete(`/jobs/${id}`);
    refreshJobs();
    toast.success("Deleted");
  };
  const onRetryJob = async (id) => {
    try {
      await api.post(`/jobs/${id}/retry`);
      refreshJobs();
      toast.success("Retrying…");
    } catch {
      toast.error("Retry failed");
    }
  };
  const onRefreshPoster = async (id) => {
    try {
      await api.post(`/jobs/${id}/refresh-poster`);
      refreshJobs();
      toast.success("Poster refreshed from video");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
  };
  const onNormalizeFrame = async (id) => {
    try {
      const t = toast.loading("Normalizing frame…");
      const r = await api.post(`/jobs/${id}/normalize-frame`);
      toast.dismiss(t);
      if (r.data?.changed === false) {
        toast.info("Already at target size");
      } else {
        toast.success("Frame normalized — clip now fills 16:9");
        refreshJobs();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Normalize failed");
    }
  };
  const onSmoothMotion = async (id) => {
    try {
      const t = toast.loading("Smoothing motion (24→30 fps)…");
      const r = await api.post(`/jobs/${id}/smooth-motion`);
      toast.dismiss(t);
      if (r.data?.changed === false) {
        toast.info("Already at 30 fps — no smoothing needed");
      } else {
        toast.success("Motion smoothed — judder removed");
        refreshJobs();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Smooth failed");
    }
  };
  const onPromoteToReel = async (id) => {
    if (
      !window.confirm(
        "Promote this clip to a Listing Reel? It'll appear in the Reels tab with its own share link and on the Realtor Dashboard. The original clip card will be removed.",
      )
    ) {
      return;
    }
    try {
      const t = toast.loading("Promoting to reel…");
      const { data } = await api.post(`/jobs/${id}/promote-to-reel`);
      toast.dismiss(t);
      toast.success(`Promoted — share link ready at ${data.share_path}`);
      refreshJobs();
      refreshReels?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    }
  };
  const onRenameJob = async (id, title) => {
    try {
      await api.patch(`/jobs/${id}/rename`, { title });
      toast.success("Clip renamed");
      refreshJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
      throw e;
    }
  };
  const onRenameReel = async (id, title) => {
    try {
      await api.patch(`/reels/${id}/rename`, { title });
      toast.success("Reel renamed");
      refreshReels?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
      throw e;
    }
  };
  const onDeleteReel = async (id) => {
    await api.delete(`/reels/${id}`);
    refreshReels();
    toast.success("Deleted");
  };
  const onRetryReel = async (id) => {
    try {
      const { data } = await api.post(`/reels/${id}/retry`);
      refreshReels();
      if (data?.recompiling) {
        toast.success(data.message || "All clips healthy — recompiling…");
      } else if (data?.retried_shots) {
        toast.success(`Retrying ${data.retried_shots} shot(s)…`);
      } else {
        toast.success("Retrying…");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };
  const onRecompileReel = async (id) => {
    try {
      await api.post(`/reels/${id}/recompile`);
      refreshReels();
      toast.success("Recompiling…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompile failed");
    }
  };
  const onUnstickReel = async (id) => {
    try {
      const { data } = await api.post(`/reels/${id}/unstick`);
      refreshReels();
      const stuck = (data?.clips || []).filter(
        (c) => c.status !== "done" || !c.has_video
      );
      if (stuck.length > 0) {
        const summary = stuck
          .map((c) => `${(c.id || "").slice(0, 8)}: ${c.status || "?"}`)
          .slice(0, 3)
          .join(", ");
        toast.error(
          `Unstuck — ${stuck.length} clip${
            stuck.length > 1 ? "s" : ""
          } not ready (${summary}). Retry the clip(s) before recompiling.`,
          { duration: 8000 }
        );
      } else {
        toast.success("Unstuck — all clips are healthy. Try Recompile.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unstick failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-[#0A0A0A] border border-white/10 rounded-xl p-1 w-fit">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-1.5 text-xs rounded-lg transition-all ${
                tab === t.id
                  ? "bg-white text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid={`library-tab-${t.id}`}
            >
              {t.label}{" "}
              <span
                className={`ml-1 ${
                  tab === t.id ? "text-black/60" : "text-zinc-600"
                }`}
              >
                ({t.count})
              </span>
            </button>
          ))}
        </div>
        {listingId && (
          <>
            <button
              onClick={() => openImportDialog("clip")}
              className="ml-auto text-[10px] tracking-[0.2em] uppercase bg-blue-500/10 hover:bg-blue-500/20 border border-blue-400/30 text-blue-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              title="Import a clip from any URL — useful for moving clips between preview and production"
              data-testid="library-import-btn"
            >
              <Icons.Download size={11} />
              Import clip
            </button>
            <button
              onClick={() => openImportDialog("reel")}
              className="text-[10px] tracking-[0.2em] uppercase bg-purple-500/10 hover:bg-purple-500/20 border border-purple-400/30 text-purple-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              title="Import a finished reel from any URL — clones the stitched video onto this listing with a fresh share link"
              data-testid="library-import-reel-btn"
            >
              <Icons.Download size={11} />
              Import reel
            </button>
            <button
              onClick={runDiagnose}
              className="text-[10px] tracking-[0.2em] uppercase bg-amber-500/10 hover:bg-amber-500/20 border border-amber-400/30 text-amber-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              title="Show the exact status of every reveal on this listing — useful when something seems stuck"
              data-testid="library-diagnose-btn"
            >
              <Icons.Stethoscope size={11} />
              Diagnose reveals
            </button>
            <button
              onClick={runCleanupZombies}
              disabled={cleanupBusy}
              className="text-[10px] tracking-[0.2em] uppercase bg-rose-500/10 hover:bg-rose-500/20 border border-rose-400/30 text-rose-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Mark any job/reel stuck in_progress for >30 minutes as failed so you can retry them"
              data-testid="library-cleanup-zombies-btn"
            >
              {cleanupBusy ? (
                <Icons.Loader2 size={11} className="animate-spin" />
              ) : (
                <Icons.Trash2 size={11} />
              )}
              Clean stuck jobs
            </button>
          </>
        )}
      </div>

      {importDialog && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !importBusy && setImportDialog(false)}
          data-testid="import-dialog-backdrop"
        >
          <div
            className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-5 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="import-dialog"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icons.Download size={14} className={importMode === "reel" ? "text-purple-300" : "text-blue-300"} />
                <span className={`text-[11px] tracking-[0.25em] uppercase ${importMode === "reel" ? "text-purple-200" : "text-blue-200"}`}>
                  {importMode === "reel" ? "Import reel from URL" : "Import clip from URL"}
                </span>
              </div>
              <button
                onClick={() => !importBusy && setImportDialog(false)}
                className="text-zinc-500 hover:text-white"
                data-testid="import-dialog-close"
              >
                <Icons.X size={14} />
              </button>
            </div>
            <div className="text-[11px] text-zinc-400 leading-snug mb-4">
              {importMode === "reel" ? (
                <>
                  Paste a direct video URL of a finished reel — typically from
                  your preview environment (e.g.{" "}
                  <code className="text-zinc-300 font-mono">
                    https://…preview…/api/public/videos/&lt;id&gt;.mp4
                  </code>
                  ). The stitched reel will be cloned onto this listing with a
                  fresh share token and an auto-extracted cover image.
                </>
              ) : (
                <>
                  Paste a direct video URL — typically from your preview
                  environment (e.g.{" "}
                  <code className="text-zinc-300 font-mono">
                    https://…preview…/api/public/videos/&lt;id&gt;.mp4
                  </code>
                  ). The clip will be downloaded and added to this listing's
                  Library as if it were generated here.
                </>
              )}
            </div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1 block">
              Source URL
            </label>
            <input
              type="url"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://…/api/public/videos/abc.mp4"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-3 font-mono"
              autoFocus
              data-testid="import-url-input"
            />
            <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1 block">
              {importMode === "reel" ? "Reel title (optional)" : "Label (optional)"}
            </label>
            <input
              type="text"
              value={importLabel}
              onChange={(e) => setImportLabel(e.target.value)}
              placeholder={
                importMode === "reel"
                  ? "108 Posthill Drive — Listing Reel"
                  : "Reveal · twilight (imported)"
              }
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-4"
              data-testid="import-label-input"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => !importBusy && setImportDialog(false)}
                className="text-[11px] uppercase tracking-wider text-zinc-400 hover:text-white px-3 py-1.5"
                data-testid="import-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitImport}
                disabled={importBusy || !importUrl.trim()}
                className={`text-[11px] uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg px-3 py-1.5 flex items-center gap-1.5 ${
                  importMode === "reel"
                    ? "bg-purple-500 hover:bg-purple-400"
                    : "bg-blue-500 hover:bg-blue-400"
                }`}
                data-testid="import-submit"
              >
                {importBusy ? (
                  <>
                    <Icons.Loader2 size={11} className="animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <Icons.Download size={11} />
                    Import
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {diagnose && (
        <div
          className="bg-[#0A0A0A] border border-amber-400/30 rounded-xl p-4"
          data-testid="diagnose-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icons.Stethoscope size={14} className="text-amber-300" />
              <span className="text-[11px] tracking-[0.25em] uppercase text-amber-200">
                Reveal health snapshot
              </span>
            </div>
            <button
              onClick={() => setDiagnose(null)}
              className="text-zinc-500 hover:text-white"
              data-testid="diagnose-close"
            >
              <Icons.X size={14} />
            </button>
          </div>
          {diagnose.loading && (
            <div className="text-xs text-zinc-400 flex items-center gap-2">
              <Icons.Loader2 size={12} className="animate-spin" />
              Fetching…
            </div>
          )}
          {diagnose.error && (
            <div className="text-xs text-red-300">Error: {diagnose.error}</div>
          )}
          {diagnose.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div className="bg-white/[0.03] border border-white/10 rounded px-2 py-1.5">
                  <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">
                    Version
                  </div>
                  <div className="text-white font-mono">
                    {diagnose.data.version}
                  </div>
                </div>
                <div className="bg-white/[0.03] border border-white/10 rounded px-2 py-1.5">
                  <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">
                    Total reveals
                  </div>
                  <div className="text-white font-mono">
                    {diagnose.data.total_reveals}
                  </div>
                </div>
                <div className="bg-white/[0.03] border border-white/10 rounded px-2 py-1.5 col-span-2 sm:col-span-2">
                  <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-500">
                    By status
                  </div>
                  <div className="text-white font-mono text-[11px]">
                    {Object.entries(diagnose.data.by_status || {})
                      .map(([k, v]) => `${k}:${v}`)
                      .join(" · ") || "—"}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                {Object.entries(diagnose.data.env || {}).map(([k, v]) => (
                  <div
                    key={k}
                    className={`rounded px-2 py-1.5 border ${
                      v === true || (typeof v === "string" && v)
                        ? "bg-emerald-500/5 border-emerald-400/20 text-emerald-200"
                        : "bg-red-500/5 border-red-400/30 text-red-200"
                    }`}
                  >
                    <div className="text-[9px] tracking-[0.2em] uppercase opacity-70">
                      {k}
                    </div>
                    <div className="font-mono">
                      {String(v === true ? "yes" : v === false ? "no" : v || "—")}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/5 pt-3">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                  Recent reveals ({diagnose.data.reveals?.length || 0})
                </div>
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  {(diagnose.data.reveals || []).map((r) => (
                    <div
                      key={r.reveal_id}
                      className={`text-[11px] border rounded px-2 py-1.5 ${
                        r.status === "failed"
                          ? "bg-red-500/5 border-red-400/20"
                          : r.status === "done"
                          ? "bg-emerald-500/5 border-emerald-400/15"
                          : "bg-amber-500/5 border-amber-400/20"
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-zinc-400">
                          {r.reveal_id.slice(0, 8)}…
                        </span>
                        <span
                          className={`font-mono ${
                            r.status === "failed"
                              ? "text-red-300"
                              : r.status === "done"
                              ? "text-emerald-300"
                              : "text-amber-300"
                          }`}
                        >
                          {r.status}
                        </span>
                        <span className="text-zinc-500">
                          {r.mode === "staging"
                            ? `stage-${r.style}`
                            : r.transform}
                        </span>
                        {r.engine && (
                          <span
                            className={`font-mono px-1.5 py-px rounded text-[10px] border ${
                              r.engine === "gemini"
                                ? "bg-blue-500/10 border-blue-400/30 text-blue-200"
                                : r.engine === "nano_banana"
                                ? "bg-fuchsia-500/10 border-fuchsia-400/30 text-fuchsia-200"
                                : "bg-emerald-500/10 border-emerald-400/30 text-emerald-200"
                            }`}
                          >
                            {r.engine === "gpt_image_2"
                              ? "openai"
                              : r.engine}
                          </span>
                        )}
                        {r.age_seconds != null && (
                          <span className="text-zinc-600">
                            · {Math.round(r.age_seconds)}s old
                          </span>
                        )}
                        {!r.image_exists && (
                          <span className="text-red-300">· image missing</span>
                        )}
                        {r.final_video_id && !r.video_exists && (
                          <span className="text-red-300">· video missing</span>
                        )}
                      </div>
                      {r.error && (
                        <div className="text-red-300 mt-1 text-[10px] leading-snug">
                          {r.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "clips" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {jobs.length === 0 && (
            <EmptyState
              icon="Film"
              text="No clips for this listing yet. Head to Single Shot."
            />
          )}
          {jobs.map((j) => (
            <ClipCard
              key={j.id}
              job={j}
              onPlay={onPlay}
              onDelete={onDeleteJob}
              onRetry={onRetryJob}
              onRefreshPoster={onRefreshPoster}
              onNormalizeFrame={onNormalizeFrame}
              onSmoothMotion={onSmoothMotion}
              onPromoteToReel={onPromoteToReel}
              onRename={onRenameJob}
              onDetails={() => setDetails({ item: j, kind: "clip" })}
            />
          ))}
        </div>
      )}

      {tab === "reels" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {reels.length === 0 && (
            <EmptyState
              icon="Layers"
              text="No reels for this listing yet."
            />
          )}
          {reels.map((r) => (
            <ReelCard
              key={r.id}
              reel={r}
              onPlay={onPlay}
              onDelete={onDeleteReel}
              onRetry={onRetryReel}
              onRecompile={onRecompileReel}
              onUnstick={onUnstickReel}
              onRename={onRenameReel}
              onDetails={() => setDetails({ item: r, kind: "reel" })}
            />
          ))}
        </div>
      )}

      <ClipDetailsModal
        item={details?.item}
        kind={details?.kind}
        onClose={() => setDetails(null)}
        onClone={(reel) => {
          setDetails(null);
          onCloneReel?.(reel);
        }}
        onPlay={onPlay}
      />
    </div>
  );
}

function EmptyState({ icon, text }) {
  const I = Icons[icon] || Icons.Inbox;
  return (
    <div className="col-span-full border border-dashed border-white/10 rounded-xl p-12 text-center">
      <I className="mx-auto text-zinc-700" size={28} />
      <div className="text-sm text-zinc-500 mt-3">{text}</div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function cleanError(err) {
  if (!err) return "";
  const s = String(err);
  if (s.toLowerCase().includes("daily cap hit on this tier")) {
    return "This tier (Lite 480p) hit its daily cap. Switch to Lite 720p or Pro 1080p — they have more headroom. Caps reset at 00:00 UTC.";
  }
  if (s.includes("daily limit") || s.toLowerCase().includes("daily limit reached")) {
    return "Magnific daily limit hit — resets at 00:00 UTC.";
  }
  if (s.includes("QUOTA_EXCEEDED") || s.toLowerCase().includes("free trial") || s.toLowerCase().includes("upgrade to a paid plan")) {
    return "Magnific plan limit reached — upgrade at magnific.com/developers/dashboard/billing to continue rendering.";
  }
  const first = s.split("\n")[0].slice(0, 240);
  if (first.includes("429")) return "Rate limit hit. Wait a moment and retry.";
  return first;
}

function ClipCard({ job, onPlay, onDelete, onRetry, onRefreshPoster, onNormalizeFrame, onSmoothMotion, onPromoteToReel, onRename, onDetails }) {
  const thumb = job.image_url ? mediaUrl(job.image_url) : null;
  const videoUrl = job.video_url ? mediaUrl(job.video_url) : null;
  const isFailed = job.status === "failed";
  // "Just arrived" pulse — green dot for clips that finished within the last
  // 30 seconds so realtors get a subtle visual confirmation that what they
  // queued has just landed in the Library.  Re-rendered every 5s via a tick
  // so the pill disappears on its own without needing a parent refetch.
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(i);
  }, []);
  const isFresh = (() => {
    if (job.status !== "done" || !job.updated_at) return false;
    try {
      const age = (Date.now() - new Date(job.updated_at).getTime()) / 1000;
      return age >= 0 && age < 30;
    } catch {
      return false;
    }
  })();
  return (
    <div
      className={`bg-[#0A0A0A] border rounded-xl overflow-hidden hover:border-white/20 transition-all group ${
        isFresh ? "border-emerald-400/60 shadow-[0_0_20px_-5px_rgba(52,211,153,0.45)]" : "border-white/10"
      }`}
      data-testid={`clip-card-${job.id}`}
    >
      <div className="relative aspect-video bg-black">
        {isFresh && (
          <div
            className="absolute top-2 left-2 z-10 flex items-center gap-1.5 bg-black/85 backdrop-blur-sm border border-emerald-400/40 rounded-full px-2 py-0.5"
            data-testid={`clip-card-${job.id}-fresh`}
            title="Just landed in your Library"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[9px] tracking-[0.2em] uppercase text-emerald-300 font-mono">
              Just arrived
            </span>
          </div>
        )}
        {videoUrl ? (
          <video
            src={videoUrl}
            poster={thumb || undefined}
            preload="metadata"
            className="w-full h-full object-cover"
            muted
            playsInline
            onMouseEnter={(e) => {
              const p = e.currentTarget.play();
              if (p && typeof p.catch === "function") p.catch(() => {});
            }}
            onMouseLeave={(e) => {
              e.currentTarget.pause();
              e.currentTarget.currentTime = 0;
            }}
          />
        ) : thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover opacity-50" />
        ) : null}
        {videoUrl && (
          <button
            onClick={() => onPlay({ url: videoUrl, title: job.template_name })}
            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-all"
          >
            <div className="bg-white text-black rounded-full p-3">
              <Icons.Play size={18} />
            </div>
          </button>
        )}
        {/* Action overlay (top-right) */}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
          <button
            onClick={onDetails}
            className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-white hover:text-black"
            title="Details"
            data-testid={`clip-details-${job.id}`}
          >
            <Icons.Info size={12} />
          </button>
          {videoUrl && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await navigator.clipboard.writeText(videoUrl);
                  toast.success("Import URL copied — paste into Production → Library → Import Clip");
                } catch {
                  // Fallback for browsers that block clipboard
                  window.prompt("Copy this URL and paste into Production → Library → Import Clip:", videoUrl);
                }
              }}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-blue-500"
              title="Copy this clip's URL — paste into Production → Library → Import Clip"
              data-testid={`clip-copy-import-${job.id}`}
            >
              <Icons.Link2 size={12} />
            </button>
          )}
          {videoUrl && onRefreshPoster && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRefreshPoster(job.id);
              }}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-emerald-500"
              title="Re-extract poster from the first frame of this video"
              data-testid={`clip-refresh-poster-${job.id}`}
            >
              <Icons.Image size={12} />
            </button>
          )}
          {videoUrl && onNormalizeFrame && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNormalizeFrame(job.id);
              }}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-amber-500"
              title="Resize this clip to true 16:9 — fills the frame edge-to-edge"
              data-testid={`clip-normalize-frame-${job.id}`}
            >
              <Icons.Maximize2 size={12} />
            </button>
          )}
          {videoUrl && onSmoothMotion && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSmoothMotion(job.id);
              }}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-cyan-500"
              title="Smooth motion — re-encodes 24→30 fps with frame blending to remove judder"
              data-testid={`clip-smooth-motion-${job.id}`}
            >
              <Icons.Zap size={12} />
            </button>
          )}
          {videoUrl && onPromoteToReel && job.model === "imported" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPromoteToReel(job.id);
              }}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-purple-500"
              title="Promote this imported clip to a Listing Reel — gets a share link and appears on the Realtor Dashboard"
              data-testid={`clip-promote-reel-${job.id}`}
            >
              <Icons.ArrowUpCircle size={12} />
            </button>
          )}
          {isFailed && (
            <button
              onClick={() => onRetry(job.id)}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-white hover:text-black"
              title="Retry"
              data-testid={`clip-retry-${job.id}`}
            >
              <Icons.RotateCw size={12} />
            </button>
          )}
          <button
            onClick={() => onDelete(job.id)}
            className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-red-500"
            title="Delete"
            data-testid={`clip-delete-${job.id}`}
          >
            <Icons.Trash2 size={12} />
          </button>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="text-xs text-white font-medium truncate flex-1 min-w-0">
            {onRename ? (
              <EditableLabel
                value={job.template_name || "Custom"}
                onSave={(v) => onRename(job.id, v)}
                inputClassName="text-xs"
                testId={`clip-rename-${job.id}`}
              />
            ) : (
              job.template_name || "Custom"
            )}
          </div>
          <StatusPill status={job.status} />
        </div>
        <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider">
          {job.model?.replace("seedance-", "")} · {job.duration}s
        </div>
        {isFailed && job.error && (
          <div className="text-[10px] text-red-400 mt-2 leading-snug line-clamp-2">
            {cleanError(job.error)}
          </div>
        )}
      </div>
    </div>
  );
}

function ReelCard({ reel, onPlay, onDelete, onRetry, onRecompile, onUnstick, onRename, onDetails }) {
  const finalUrl = reel.final_video_url ? mediaUrl(reel.final_video_url) : null;
  const posterUrl = reel.cover_image_url ? mediaUrl(reel.cover_image_url) : null;
  const isFailed = reel.status === "failed";
  const isStitching = reel.status === "stitching";
  // Tick to auto-fade the "Just arrived" pill at the 30s mark.
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(i);
  }, []);
  // "Just arrived" pulse for reels that finished stitching within 30 seconds.
  const isFresh = (() => {
    if (reel.status !== "done" || !reel.updated_at) return false;
    try {
      const age = (Date.now() - new Date(reel.updated_at).getTime()) / 1000;
      return age >= 0 && age < 30;
    } catch {
      return false;
    }
  })();
  const [views, setViews] = useState(null);
  const [lastView, setLastView] = useState(null);
  useEffect(() => {
    if (reel.status !== "done" || !reel.share_token) return;
    let cancel = false;
    api
      .get(`/reels/${reel.id}/views`)
      .then(({ data }) => {
        if (cancel) return;
        setViews(data.count);
        setLastView(data.recent?.[0] || null);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [reel.id, reel.status, reel.share_token]);
  return (
    <div
      className={`bg-[#0A0A0A] border rounded-xl overflow-hidden hover:border-white/20 transition-all group ${
        isFresh ? "border-emerald-400/60 shadow-[0_0_20px_-5px_rgba(52,211,153,0.45)]" : "border-white/10"
      }`}
      data-testid={`reel-card-${reel.id}`}
    >
      <div className="relative aspect-video bg-black">
        {isFresh && (
          <div
            className="absolute top-2 left-2 z-10 flex items-center gap-1.5 bg-black/85 backdrop-blur-sm border border-emerald-400/40 rounded-full px-2 py-0.5"
            data-testid={`reel-card-${reel.id}-fresh`}
            title="Just landed in your Library"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[9px] tracking-[0.2em] uppercase text-emerald-300 font-mono">
              Just arrived
            </span>
          </div>
        )}
        {finalUrl ? (
          <video
            src={finalUrl}
            poster={posterUrl || undefined}
            preload="metadata"
            className="w-full h-full object-cover"
            muted
            playsInline
            onMouseEnter={(e) => {
              const p = e.currentTarget.play();
              if (p && typeof p.catch === "function") p.catch(() => {});
            }}
            onMouseLeave={(e) => {
              e.currentTarget.pause();
              e.currentTarget.currentTime = 0;
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-700">
            <Icons.Film size={28} />
          </div>
        )}
        {finalUrl && (
          <button
            onClick={() => onPlay({ url: finalUrl, title: reel.title })}
            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-all"
          >
            <div className="bg-white text-black rounded-full p-3">
              <Icons.Play size={18} />
            </div>
          </button>
        )}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100">
          <button
            onClick={onDetails}
            className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-white hover:text-black"
            title="Details & shots"
            data-testid={`reel-details-${reel.id}`}
          >
            <Icons.Info size={12} />
          </button>
          {finalUrl && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await navigator.clipboard.writeText(finalUrl);
                  toast.success("Reel URL copied — paste into Production → Library → Import reel");
                } catch {
                  window.prompt(
                    "Copy this URL and paste into Production → Library → Import reel:",
                    finalUrl,
                  );
                }
              }}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-purple-500"
              title="Copy this reel's URL — paste into Production → Library → Import reel"
              data-testid={`reel-copy-import-${reel.id}`}
            >
              <Icons.Link2 size={12} />
            </button>
          )}
          {reel.status === "done" && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const { data } = await api.post(`/reels/${reel.id}/share`);
                  const url = `${window.location.origin}${data.share_path}`;
                  await navigator.clipboard.writeText(url);
                  toast.success("Share link copied");
                } catch {
                  toast.error("Could not create share link");
                }
              }}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-white hover:text-black"
              title="Copy public share link"
              data-testid={`reel-share-${reel.id}`}
            >
              <Icons.Share2 size={12} />
            </button>
          )}
          {reel.status === "done" && (
            <ExportMenu reelId={reel.id} />
          )}
          {isFailed && (
            <button
              onClick={() => onRetry(reel.id)}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-white hover:text-black"
              title="Retry failed shots"
              data-testid={`reel-retry-${reel.id}`}
            >
              <Icons.RotateCw size={12} />
            </button>
          )}
          {(isFailed || isStitching) && onRecompile && (
            <button
              onClick={() => onRecompile(reel.id)}
              className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-white hover:text-black"
              title="Re-run ffmpeg stitch (does not regenerate clips)"
              data-testid={`reel-recompile-${reel.id}`}
            >
              <Icons.Wand2 size={12} />
            </button>
          )}
          {isStitching && onUnstick && (
            <button
              onClick={() => onUnstick(reel.id)}
              className="p-1.5 bg-amber-500/90 backdrop-blur rounded text-black hover:bg-amber-400"
              title="Force-fail this stuck reel and tell me which clip is the blocker"
              data-testid={`reel-unstick-${reel.id}`}
            >
              <Icons.Unplug size={12} />
            </button>
          )}
          <button
            onClick={() => onDelete(reel.id)}
            className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-red-500"
          >
            <Icons.Trash2 size={12} />
          </button>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="text-sm text-white font-medium truncate flex-1 min-w-0">
            {onRename ? (
              <EditableLabel
                value={reel.title}
                onSave={(v) => onRename(reel.id, v)}
                inputClassName="text-sm"
                testId={`reel-rename-${reel.id}`}
              />
            ) : (
              reel.title
            )}
          </div>
          <StatusPill status={reel.status} />
          <DashboardVisibilityChip visibility={reel.dashboard_visibility} />
        </div>
        {reel.aspect_variants && Object.keys(reel.aspect_variants).length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {Object.entries(reel.aspect_variants).map(([aspect, v]) => (
              <a
                key={aspect}
                href={mediaUrl(v.video_url)}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors inline-flex items-center gap-1"
                title={`${v.label} variant — click to download`}
                data-testid={`reel-aspect-${aspect}-${reel.id}`}
              >
                <Icons.Crop size={9} />
                {aspect.replace("_", ":")}
              </a>
            ))}
          </div>
        )}
        <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider flex items-center gap-3">
          <span>
            {reel.shot_job_ids?.length || 0} shots ·{" "}
            {reel.model?.replace("seedance-", "")}
          </span>
          {views !== null && views > 0 && (
            <span
              className="flex items-center gap-1 text-zinc-400"
              data-testid={`reel-views-${reel.id}`}
              title={
                lastView
                  ? `${views} view${views === 1 ? "" : "s"} · last ${timeAgo(lastView)}`
                  : `${views} share-link view${views === 1 ? "" : "s"}`
              }
            >
              <Icons.Eye size={10} />
              {views}
              {lastView && (
                <span className="text-zinc-600 normal-case tracking-normal">
                  · {timeAgo(lastView)}
                </span>
              )}
            </span>
          )}
        </div>
        {reel.status === "failed" && reel.error && (
          <div className="text-[10px] text-red-400 mt-2 leading-snug line-clamp-2">
            {reel.error.includes("shots failed")
              ? "Magnific rate limit hit on some shots. Click ↻ to retry only the failed ones."
              : reel.error}
          </div>
        )}
      </div>
    </div>
  );
}

function ExportMenu({ reelId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const exportReel = async (preset, label) => {
    setBusy(preset);
    try {
      const { data } = await api.post(`/reels/${reelId}/export/${preset}`);
      // Download the resulting video via a temporary anchor
      const url = mediaUrl(data.video_url);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reel-${label}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(
        data.cached ? `Re-using ${label} export` : `${label} export ready`
      );
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 bg-black/80 backdrop-blur rounded text-white hover:bg-white hover:text-black"
        title="Export for social (9:16 / 1:1 / 16:9)"
        data-testid={`reel-export-${reelId}`}
      >
        <Icons.Download size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 bg-[#0A0A0A] border border-white/15 rounded-lg shadow-2xl overflow-hidden min-w-[160px]">
          {[
            { id: "9_16", label: "9:16 · IG / TikTok", icon: "Smartphone" },
            { id: "1_1", label: "1:1 · Facebook", icon: "Square" },
            { id: "16_9", label: "16:9 · YouTube", icon: "Monitor" },
          ].map((opt) => {
            const I = Icons[opt.icon] || Icons.Film;
            return (
              <button
                key={opt.id}
                onClick={() => exportReel(opt.id, opt.label.split(" · ")[0])}
                disabled={!!busy}
                className="w-full flex items-center gap-2 text-left px-3 py-2 text-xs text-white hover:bg-white/5 disabled:opacity-50"
                data-testid={`reel-export-${reelId}-${opt.id}`}
              >
                {busy === opt.id ? (
                  <Icons.Loader2 size={11} className="animate-spin" />
                ) : (
                  <I size={11} className="text-zinc-400" />
                )}
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Tiny chip on each reel card answering "is this reel showing on the
 * realtor's public dashboard?".  Made because the realtor's expectation
 * is that any library reel should appear — but the dashboard requires:
 *   - linked to a listing
 *   - listing.status === "active"
 *   - reel has share_token, status===done, final_video_id
 * If anything's missing, this chip surfaces the reason inline so the user
 * can fix it instead of being puzzled.
 */
function DashboardVisibilityChip({ visibility }) {
  if (!visibility) return null;
  if (visibility.visible) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
        data-testid="reel-on-dashboard"
        title="Showing on the realtor's public dashboard"
      >
        <Icons.CheckCircle2 size={9} />
        Live
      </span>
    );
  }
  const reason = (visibility.reasons || [])[0] || "hidden";
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-400/30 bg-amber-500/10 text-amber-300"
      data-testid="reel-not-on-dashboard"
      title={`Not on dashboard: ${(visibility.reasons || []).join(", ")}`}
    >
      <Icons.EyeOff size={9} />
      {reason}
    </span>
  );
}

